import React from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { TabsContent } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Separator } from '../ui/separator';
import { CheckCircle, RefreshCw, Plus, Clock, Layers } from 'lucide-react';
import SiretInput from '../ui/SiretInput';
import { TERRITOIRES_DOM, LotForm } from './constants';

export default function ProgrammesTab({ ctx }) {
  const { saving, programmeData, setProgrammeData, programmeResult, programmes, handleCreateProgramme, addLot, removeLot, updateLot, totalLots } = ctx;
  return (
        <TabsContent value="programmes" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Formulaire Programme */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Layers className="h-5 w-5 text-emerald-600" />
                  Nouveau Programme Multi-lots
                </CardTitle>
                <CardDescription>
                  Créez un programme avec extension décennale (10 ans)
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Infos programme */}
                <div className="space-y-4">
                  <div>
                    <Label>Nom du programme *</Label>
                    <Input
                      value={programmeData.nom_programme}
                      onChange={(e) => setProgrammeData(prev => ({ ...prev, nom_programme: e.target.value }))}
                      placeholder="Ex: Résidence Les Palmiers"
                      className="mt-1"
                      data-testid="input-nom-programme"
                    />
                  </div>
                  
                  <div>
                    <Label>Territoire DOM</Label>
                    <Select 
                      value={programmeData.territoire} 
                      onValueChange={(v) => setProgrammeData(prev => ({ ...prev, territoire: v }))}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TERRITOIRES_DOM.map((t) => (
                          <SelectItem key={t.value} value={t.value}>
                            <div className="flex items-center gap-2">
                              <span>{t.emoji}</span>
                              <span>{t.label}</span>
                            </div>
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <Separator />
                
                {/* Maître d'ouvrage */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-gray-700">Maître d&apos;ouvrage</h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Nom / Raison sociale *</Label>
                      <Input
                        value={programmeData.maitre_ouvrage_nom}
                        onChange={(e) => setProgrammeData(prev => ({ ...prev, maitre_ouvrage_nom: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <SiretInput
                        value={programmeData.maitre_ouvrage_siret}
                        onChange={(value) => setProgrammeData(prev => ({ ...prev, maitre_ouvrage_siret: value }))}
                        label="SIRET"
                        onAutoComplete={(data) => {
                          setProgrammeData(prev => ({
                            ...prev,
                            maitre_ouvrage_nom: data.raison_sociale || prev.maitre_ouvrage_nom,
                            maitre_ouvrage_adresse: data.adresse ? `${data.adresse}, ${data.code_postal} ${data.ville}` : prev.maitre_ouvrage_adresse
                          }));
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={programmeData.maitre_ouvrage_email}
                      onChange={(e) => setProgrammeData(prev => ({ ...prev, maitre_ouvrage_email: e.target.value }))}
                      className="mt-1"
                    />
                  </div>
                </div>
                
                <Separator />
                
                {/* Lots */}
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-gray-700">Lots du programme</h4>
                    <Button variant="outline" size="sm" onClick={addLot}>
                      <Plus className="h-4 w-4 mr-1" />
                      Ajouter un lot
                    </Button>
                  </div>
                  
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {programmeData.lots.map((lot, index) => (
                      <LotForm
                        key={index}
                        lot={lot}
                        index={index}
                        onChange={(updated) => updateLot(index, updated)}
                        onRemove={() => removeLot(index)}
                      />
                    ))}
                  </div>
                  
                  {/* Total */}
                  <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200">
                    <div className="flex justify-between items-center">
                      <span className="font-medium text-emerald-800">Total des lots</span>
                      <span className="text-xl font-bold text-emerald-700">
                        {totalLots.toLocaleString('fr-FR')} € (Montant de base)
                      </span>
                    </div>
                    <div className="text-sm text-emerald-600 mt-1">
                      {programmeData.lots.length} lot(s)
                    </div>
                  </div>
                </div>
                
                <Button 
                  onClick={handleCreateProgramme}
                  disabled={saving || !programmeData.nom_programme || totalLots <= 0}
                  className="w-full bg-emerald-600 hover:bg-emerald-700"
                  data-testid="btn-create-programme"
                >
                  {saving ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  )}
                  Créer le programme
                </Button>
              </CardContent>
            </Card>
            
            {/* Résultat Programme */}
            <div className="space-y-6">
              {programmeResult?.success && (
                <Card className="border-2 border-emerald-200 bg-emerald-50/50">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-emerald-600" />
                      Programme créé
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="bg-white p-4 rounded-xl border">
                      <div className="text-sm text-gray-500">Référence</div>
                      <div className="text-xl font-bold text-emerald-700">
                        {programmeResult.programme?.reference}
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4">
                      <div className="bg-white p-4 rounded-xl border">
                        <div className="text-sm text-gray-500">Contribution programme</div>
                        <div className="text-xl font-bold text-emerald-700">
                          {programmeResult.programme?.calcul?.contribution_programme?.toLocaleString('fr-FR')} €
                        </div>
                      </div>
                      <div className="bg-white p-4 rounded-xl border">
                        <div className="text-sm text-gray-500">Dotation décennale</div>
                        <div className="text-xl font-bold text-amber-600">
                          {programmeResult.programme?.calcul?.dotation_decennale?.toLocaleString('fr-FR')} €
                        </div>
                      </div>
                    </div>
                    
                    <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-2 text-blue-800">
                        <Clock className="h-5 w-5" />
                        <div>
                          <div className="font-semibold">Extension décennale : 10 ans</div>
                          <div className="text-sm">La garantie décennale s&apos;active à la réception des travaux</div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Liste des programmes existants */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Programmes existants</CardTitle>
                </CardHeader>
                <CardContent>
                  {programmes.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Layers className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>Aucun programme créé</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {programmes.slice(0, 5).map((prog) => (
                        <div key={prog.id} className="p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                          <div className="flex justify-between items-start">
                            <div>
                              <div className="flex items-center gap-2">
                                <Badge className="bg-emerald-700 text-white font-semibold">
                                  {prog.reference}
                                </Badge>
                                <span className="font-medium">{prog.nom}</span>
                              </div>
                              <div className="text-sm text-gray-500 mt-1">
                                {prog.nb_lots} lots • {prog.calcul?.contribution_programme?.toLocaleString('fr-FR')} €
                              </div>
                            </div>
                            <Badge variant="outline">
                              {prog.decennale?.statut_decennale || 'En attente'}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
  );
}
